/**
 * Wave 3.80 — Fixed sub-category ordering for Diamond raw material.
 *
 * Business rule (must be enforced consistently across every UI that
 * lists diamond classifications or requirements): the sub-categories
 * are always rendered in this canonical order regardless of how
 * operators uploaded them.
 *
 *   1. Polki               (schema_code prefix POL-*)
 *   2. Diamonds (Dia)      (schema_code prefix DIA-*)
 *   3. Rose Cuts (RC)      (schema_code prefix RC-*)
 *   4. Brown Diamonds (BD) (schema_code prefix BD-*)
 *   5. Chakri (CHK)        (schema_code prefix CHK-*)
 *   6. Other               (fallback bucket, always last)
 *
 * The Python mirror lives in ``backend/core/diamond_hierarchy.py`` —
 * KEEP THESE TWO IN SYNC when the taxonomy changes.
 */

// Multi-prefix taxonomy — each sub-category accepts a set of prefix
// spellings so operators can use either the abbreviated form (`POL`,
// `DIA`, `RC`) or the full spelling (`POLKI`, `DIAMOND`, `ROSE`). Longer
// prefixes are listed first inside each entry so `POLKI-D1` matches
// `POLKI` (rank 0) instead of tripping on the `POL` guard that (correctly)
// rejects `POLYP-01`.
//
// Order across sub-categories defines the canonical display order —
// DO NOT re-order without a business owner sign-off.
export const DIAMOND_SUBCATEGORIES = [
  { prefixes: ['POLKI', 'POL'],                          label: 'Polki' },
  { prefixes: ['DIAMONDS', 'DIAMOND', 'DIA'],            label: 'Diamonds' },
  { prefixes: ['ROSECUTS', 'ROSECUT', 'ROSE', 'RC'],     label: 'Rose Cuts' },
  { prefixes: ['BROWN', 'BD'],                           label: 'Brown Diamonds' },
  { prefixes: ['CHAKRI', 'CHK'],                         label: 'Chakri' },
];
export const OTHER_LABEL = 'Other';
export const OTHER_RANK = DIAMOND_SUBCATEGORIES.length;

/**
 * Extract a schema_code-ish string from a requirement / classification
 * / packet item. Handles:
 *   - Raw strings (``"POL-01"``)
 *   - Classification docs (``{ schema_code }``)
 *   - Requirement rows / PDF entries (``{ classification_label }``)
 *   - Packets with a nested classification (``{ classification: { schema_code } }``)
 *   - Packets with a flattened schema (``{ classification_schema }``)
 *
 * @param {*} item
 * @returns {string} best-effort schema code (may be empty).
 */
export function extractSchemaCode(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item !== 'object') return '';
  if (item.schema_code) return String(item.schema_code);
  // Packet payloads returned by /diamonds/packets embed the linked
  // classification as an object.
  if (item.classification && item.classification.schema_code) {
    return String(item.classification.schema_code);
  }
  if (item.classification_schema) return String(item.classification_schema);
  const label = item.classification_label || item.label || '';
  if (!label) return '';
  // ``classification_label`` is always formatted as
  // "<schema_code> — <shape_name>" when a classification is linked.
  const head = String(label).split('—')[0].split('-')[0].trim();
  return head || String(label);
}

/**
 * Return ``[rank, subcategoryLabel]`` for a diamond item.
 *
 * @param {*} itemOrCode - schema_code string, classification / requirement
 *   dict, or nullish.
 * @returns {[number, string]}
 */
export function hierarchyRank(itemOrCode) {
  const code = extractSchemaCode(itemOrCode);
  if (!code) return [OTHER_RANK, OTHER_LABEL];
  const up = code.trim().toUpperCase();
  // Multi-prefix support (Wave 3.80 hotfix). Longer prefixes are
  // checked first inside each sub-category so `POLKI-D1` matches
  // `POLKI` (rank 0) before the `POL` guard fires.
  for (let idx = 0; idx < DIAMOND_SUBCATEGORIES.length; idx += 1) {
    const { prefixes, label } = DIAMOND_SUBCATEGORIES[idx];
    for (const prefix of prefixes) {
      // Fast path: exact match or `PREFIX-` boundary.
      if (up === prefix || up.startsWith(`${prefix}-`)) {
        return [idx, label];
      }
      // Fallback: `PREFIX<digit>` form (e.g. `DIA01`). Guard against
      // `PREFIX<alpha>` false positives (`POLYP` shouldn't match `POL`
      // — but `POLKI` is caught by the longer prefix earlier in the
      // list).
      if (up.startsWith(prefix)) {
        const tail = up.charAt(prefix.length);
        if (!tail || !/[A-Z]/.test(tail)) return [idx, label];
      }
    }
  }
  return [OTHER_RANK, OTHER_LABEL];
}

/**
 * Stable sort primarily by diamond hierarchy rank, secondarily by the
 * caller-provided ``secondaryKey`` (defaults to ``sieves`` → ``size``
 * → ``schema_code``).
 *
 * @param {Array} items
 * @param {(item: any) => (string|Array<string>)} [secondaryKey]
 * @returns {Array} new sorted array (input is not mutated).
 */
export function sortDiamondItems(items, secondaryKey) {
  if (!Array.isArray(items)) return items;
  const defaultKey = (it) => {
    if (it && typeof it === 'object') {
      return [
        (it.sieves || '').toLowerCase(),
        (it.size || '').toLowerCase(),
        (it.schema_code || '').toLowerCase(),
        (it.classification_label || '').toLowerCase(),
      ].join('|');
    }
    return String(it).toLowerCase();
  };
  const keyFn = secondaryKey || defaultKey;
  // Decorate-sort-undecorate keeps the sort stable across all browsers
  // and avoids re-running hierarchyRank inside the compare function.
  return items
    .map((it, idx) => ({ it, idx, rank: hierarchyRank(it)[0], sec: keyFn(it) }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (a.sec < b.sec) return -1;
      if (a.sec > b.sec) return 1;
      return a.idx - b.idx;
    })
    .map((row) => row.it);
}

/**
 * Group a list of items into buckets keyed by sub-category (in the
 * canonical order). Each bucket item is ``{ rank, label, items[] }``;
 * empty buckets are omitted.
 *
 * @param {Array} items
 * @returns {Array<{ rank: number, label: string, items: any[] }>}
 */
export function groupDiamondBySubcategory(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const buckets = new Map();
  for (const it of items) {
    const [rank, label] = hierarchyRank(it);
    const key = rank;
    if (!buckets.has(key)) buckets.set(key, { rank, label, items: [] });
    buckets.get(key).items.push(it);
  }
  return Array.from(buckets.values()).sort((a, b) => a.rank - b.rank);
}
